export { ONE_WHO_TRIED_THRESHOLDS, ONE_WHO_TRIED_GATE_HOURS, MS_PER_HOUR as ONE_WHO_TRIED_MS_PER_HOUR, getOneWhoTriedMultiplier, getActiveOneWhoTriedEffects, getNewlyCrossedThresholds, } from './oneWhoTried.js';
import { getOneWhoTriedMultiplier } from './oneWhoTried.js';
// ── Vocations (v2 Softening) ──
//
// Canonical spec: `docs/design/vocations.md`.
// GDD tie-ins:    `docs/GDD.md` §6.5 (display rules) and §16.14 (static order).
//
// Design rules enforced by this module:
//   1. No stat sheet. This module intentionally exposes only `displayName` +
//      `shortDescription` — the exact text a player sees at character creation.
//   2. No numeric effect values ship in metadata. Effect-math lives in
//      per-category `applyVocationEffect_*` functions, which are currently
//      identity stubs to be implemented in the Vocations-runtime phase.
//   3. Order is static per §16.14 (overrides §3 of vocations.md, which said
//      "shuffled per session"). The list order is curated thematically below.
/**
 * The twelve Vocations. String values are the lowercase poetic form used as
 * stable identifiers in save data, analytics, and server ledgers.
 *
 * TypeScript identifiers are chosen to be valid enum keys; the player-facing
 * name lives in {@link VocationMetadata.displayName}. In particular,
 * "The One Who Tried" maps to the identifier {@link VocationKey.OneWhoTried}
 * and the string value `'one-who-tried'`.
 */
export var VocationKey;
(function (VocationKey) {
    VocationKey["Sleeper"] = "sleeper";
    VocationKey["Cook"] = "cook";
    VocationKey["Builder"] = "builder";
    VocationKey["Keeper"] = "keeper";
    VocationKey["Weaver"] = "weaver";
    VocationKey["Walker"] = "walker";
    VocationKey["Pilgrim"] = "pilgrim";
    VocationKey["Listener"] = "listener";
    VocationKey["Weeper"] = "weeper";
    VocationKey["Warden"] = "warden";
    VocationKey["Fool"] = "fool";
    VocationKey["OneWhoTried"] = "one-who-tried";
})(VocationKey || (VocationKey = {}));
/**
 * All twelve Vocations keyed by {@link VocationKey}. Display text is verbatim
 * from `docs/design/vocations.md` §2.
 */
export const VOCATIONS = {
    [VocationKey.Sleeper]: {
        key: VocationKey.Sleeper,
        displayName: 'The Sleeper',
        shortDescription: 'Rests well.',
    },
    [VocationKey.Cook]: {
        key: VocationKey.Cook,
        displayName: 'The Cook',
        shortDescription: 'Nourishes others.',
    },
    [VocationKey.Builder]: {
        key: VocationKey.Builder,
        displayName: 'The Builder',
        shortDescription: 'Works with wood.',
    },
    [VocationKey.Keeper]: {
        key: VocationKey.Keeper,
        displayName: 'The Keeper',
        shortDescription: 'Keeps things.',
    },
    [VocationKey.Weaver]: {
        key: VocationKey.Weaver,
        displayName: 'The Weaver',
        shortDescription: 'Joins things.',
    },
    [VocationKey.Walker]: {
        key: VocationKey.Walker,
        displayName: 'The Walker',
        shortDescription: 'Goes far.',
    },
    [VocationKey.Pilgrim]: {
        key: VocationKey.Pilgrim,
        displayName: 'The Pilgrim',
        shortDescription: 'Walks to shrines.',
    },
    [VocationKey.Listener]: {
        key: VocationKey.Listener,
        displayName: 'The Listener',
        shortDescription: 'Hears the wind.',
    },
    [VocationKey.Weeper]: {
        key: VocationKey.Weeper,
        displayName: 'The Weeper',
        shortDescription: 'Mourns what was.',
    },
    [VocationKey.Warden]: {
        key: VocationKey.Warden,
        displayName: 'The Warden',
        shortDescription: 'Stands still.',
    },
    [VocationKey.Fool]: {
        key: VocationKey.Fool,
        displayName: 'The Fool',
        shortDescription: 'Is always wrong.',
    },
    [VocationKey.OneWhoTried]: {
        key: VocationKey.OneWhoTried,
        displayName: 'The One Who Tried',
        shortDescription: 'Does their best.',
    },
};
/**
 * Static display order for the character-creation screen.
 *
 * Required by GDD §16.14 ("Vocations display: static order, not shuffled",
 * which overrides vocations.md §3's shuffled-per-session note).
 *
 * Ordering rationale — thematic grouping, gentle on-ramp first:
 *   1. Domestic / hearth-centered (the player imagines a home):
 *        Sleeper, Cook, Builder, Keeper, Weaver
 *   2. Traveling / outward-facing (the player imagines a road):
 *        Walker, Pilgrim, Listener
 *   3. Contemplative / rare (the player imagines a long patience):
 *        Weeper, Warden, Fool, OneWhoTried
 *
 * The order is intentionally deterministic so the community can reference
 * "slot 7" unambiguously across sessions, patches, and platforms.
 */
export const VOCATION_DISPLAY_ORDER = [
    // Domestic / hearth-centered
    VocationKey.Sleeper,
    VocationKey.Cook,
    VocationKey.Builder,
    VocationKey.Keeper,
    VocationKey.Weaver,
    // Traveling / outward-facing
    VocationKey.Walker,
    VocationKey.Pilgrim,
    VocationKey.Listener,
    // Contemplative / rare
    VocationKey.Weeper,
    VocationKey.Warden,
    VocationKey.Fool,
    VocationKey.OneWhoTried,
];
/**
 * Lookup helper for Vocation metadata.
 *
 * @param key - A {@link VocationKey} value.
 * @returns The corresponding {@link VocationMetadata} entry.
 */
export function getVocation(key) {
    return VOCATIONS[key];
}
// ── Hidden effect stubs ───────────────────────────────────────────────────
//
// One identity function per effect category called out in vocations.md §2.
// They all currently return their input unmodified. Runtime logic will be
// wired in the Vocations-runtime phase; until then these exist so callers
// can route through the final signature and no site has to be rewritten
// later.
//
// IMPORTANT: any change here must preserve the "identity by default" invariant
// covered in the paired test file — accidental non-identity math is a
// regression we never want to ship.
/**
 * Material gather-rate multiplier hook.
 *
 * Applies to any Vocation that quietly modifies how quickly materials are
 * gathered from the world (Sleeper's ~3% penalty, per vocations.md §2.1).
 *
 * @param v - The Vocation performing the gather.
 * @param base - Base gather rate in world-units/sec (or per-tick).
 * @returns The adjusted rate. Currently identity.
 */
/** Sleeper's across-the-board penalty multiplier (vocations.md §2.1). */
export const SLEEPER_PENALTY_MULTIPLIER = 0.97;
export function applyVocationEffect_gatherRate(v, base) {
    if (v === VocationKey.Sleeper)
        return base * SLEEPER_PENALTY_MULTIPLIER;
    return base;
}
/**
 * Crafting-speed multiplier.
 *
 * Sleeper-only per vocations.md §2.1: −3% crafting speed.
 *
 * @param v - The Vocation crafting.
 * @param base - Base craft speed or inverse duration.
 * @returns The adjusted speed. ×0.97 for Sleeper, identity for others.
 */
export function applyVocationEffect_craftSpeed(v, base) {
    if (v === VocationKey.Sleeper)
        return base * SLEEPER_PENALTY_MULTIPLIER;
    return base;
}
/**
 * Combat-damage multiplier.
 *
 * Sleeper-only per vocations.md §2.1: −3% combat damage.
 *
 * @param v - The Vocation dealing damage.
 * @param base - Base damage value.
 * @returns The adjusted damage. ×0.97 for Sleeper, identity for others.
 */
export function applyVocationEffect_combatDamage(v, base) {
    if (v === VocationKey.Sleeper)
        return base * SLEEPER_PENALTY_MULTIPLIER;
    return base;
}
/**
 * Nightmare-event immunity check.
 *
 * Per vocations.md §2.1: Sleepers are fully immune to Nightmare events.
 * When a Nightmare fires at a hearth, if any occupant is a Sleeper the
 * attendance-accumulator reset is skipped for that hearth.
 *
 * @param v - The Vocation of the hearth occupant.
 * @returns true if the Vocation is immune to Nightmares.
 */
export function isNightmareImmune(v) {
    return v === VocationKey.Sleeper;
}
/** Cook self-consumption debuff multiplier (vocations.md §2.2). */
export const COOK_SELF_BUFF_MULTIPLIER = 0.8;
/** Cook witness-consumption buff multiplier (vocations.md §2.2). */
export const COOK_WITNESS_BUFF_MULTIPLIER = 1.3;
/**
 * Cooked-food buff-strength modifier.
 *
 * Covers Cook's asymmetric buff shaping (weaker for self, stronger for
 * witnesses — vocations.md §2.2) and any future Vocation that tweaks food.
 *
 * @param cookVocation - The Vocation of the character who cooked the food.
 * @param base - Base buff strength.
 * @param eaterRelation - How the eater relates to the cook event.
 * @returns The adjusted buff strength.
 */
export function applyVocationEffect_cookQuality(cookVocation, base, eaterRelation = 'other') {
    if (cookVocation !== VocationKey.Cook)
        return base;
    if (eaterRelation === 'self')
        return base * COOK_SELF_BUFF_MULTIPLIER;
    if (eaterRelation === 'witness')
        return base * COOK_WITNESS_BUFF_MULTIPLIER;
    return base;
}
/**
 * Home-weakening timer multiplier.
 *
 * Builder-only per vocations.md §2.3: Builder structures never weaken from
 * neglect. Implementation will multiply (or zero) the weakening-tick rate.
 *
 * @param v - The Vocation that built the home.
 * @param base - Base weakening timer in seconds.
 * @returns The adjusted timer. Currently identity.
 */
export function applyVocationEffect_homeWeakenDelay(_v, base) {
    // TODO(phase-Vocations-runtime): implement per vocations.md §2.
    return base;
}
/**
 * Offline ghost-decay multiplier.
 *
 * Walker-only per vocations.md §2.4: ghost decay does not apply while the
 * Walker is offline. Returns 0 for Walker (no decay), identity for others.
 *
 * @param v - The Vocation of the offline player.
 * @param base - Base decay rate.
 * @returns The adjusted decay rate. 0 for Walker, identity for others.
 */
export function applyVocationEffect_ghostDecayRate(v, base) {
    if (v === VocationKey.Walker)
        return 0;
    return base;
}
/** Listener foreshadow lead time in milliseconds (~30 real minutes). */
export const LISTENER_WEATHER_LEAD_MS = 30 * 60 * 1_000;
/**
 * Weather-preview lead time.
 *
 * Listener-only per vocations.md §2.5: weather/siege cues surface roughly
 * 30 real minutes earlier. `base` is the lead time in milliseconds (0 for
 * most Vocations).
 *
 * @param v - The Vocation observing the weather.
 * @param base - Base lead time in ms (0 for most Vocations).
 * @returns The adjusted lead time. +30 min for Listener, identity for others.
 */
export function applyVocationEffect_weatherPreviewLead(v, base) {
    if (v === VocationKey.Listener)
        return base + LISTENER_WEATHER_LEAD_MS;
    return base;
}
/**
 * Sapling growth-rate multiplier.
 *
 * Weeper-only per vocations.md §2.6: saplings the Weeper stands near grow
 * ~10% faster.
 *
 * @param v - The Vocation near the sapling.
 * @param base - Base growth rate.
 * @returns The adjusted growth rate. ×1.1 for Weeper, identity for others.
 */
export function applyVocationEffect_saplingGrowthRate(v, base) {
    if (v === VocationKey.Weeper)
        return base * 1.1;
    return base;
}
/**
 * Restoration-contribution multiplier.
 *
 * Weeper-only per vocations.md §2.6: restoration meter contributions are
 * 1.2× baseline in any realm the Weeper actively plays in.
 *
 * @param v - The Vocation contributing to restoration.
 * @param base - Base contribution delta (percentage points).
 * @returns The adjusted delta. ×1.2 for Weeper, identity for others.
 */
export function applyVocationEffect_restorationContribution(v, base) {
    if (v === VocationKey.Weeper)
        return base * 1.2;
    return base;
}
/**
 * Fool recipe-discovery / craft-failure rate.
 *
 * Fool-only per vocations.md §2.7: every 5th craft produces an unintended
 * item (usually worse, occasionally a unique recipe discovery).
 *
 * @param v - The Vocation crafting.
 * @param base - Base failure / discovery probability (0 for most Vocations).
 * @returns The adjusted probability. Currently identity.
 */
export function applyVocationEffect_craftFailureRate(v, base) {
    // Fool: every 5th craft triggers a discovery roll. The actual counter
    // and roll logic live in FoolCraftService (server-side); this function
    // returns a non-zero signal so callers know to check the service.
    if (v === VocationKey.Fool)
        return 1;
    return base;
}
/**
 * Inventory-capacity multiplier.
 *
 * Keeper-only per vocations.md §2.8: +25% material inventory capacity. The
 * straight-man Vocation — no hidden twist.
 *
 * @param v - The Vocation owning the inventory.
 * @param base - Base material capacity.
 * @returns The adjusted capacity. ×1.25 for Keeper, identity for others.
 */
export function applyVocationEffect_inventoryCapacity(v, base) {
    if (v === VocationKey.Keeper)
        return Math.floor(base * 1.25);
    return base;
}
/**
 * Shrine-ritual tick-rate multiplier.
 *
 * Pilgrim-only per vocations.md §2.9: rituals tick ~10% faster, but only at
 * shrines the character physically walked to. Callers must provide the
 * walked-vs-fast-traveled state; this stub ignores it for now.
 *
 * @param v - The Vocation at the shrine.
 * @param base - Base tick rate.
 * @returns The adjusted tick rate. Currently identity.
 */
export function applyVocationEffect_shrineTickRate(v, base) {
    // vocations.md §2.9 — Pilgrim shrine rituals tick 10% faster.
    // The walked-vs-fast-traveled gate is applied at the callsite via
    // PilgrimTrackingService.getRitualBonus(); this function only checks
    // vocation identity.
    return v === VocationKey.Pilgrim ? base * 1.10 : base;
}
/** Weaver crane drift-speed multiplier per vocations.md §2.10. */
export const WEAVER_CRANE_SPEED_MULTIPLIER = 2;
/** Baseline recipient cap for non-Weaver cranes. */
export const BASELINE_CRANE_RECIPIENT_CAP = 1;
/** Weaver recipient cap per vocations.md §2.10. */
export const WEAVER_CRANE_RECIPIENT_CAP = 3;
/**
 * Paper-crane drift-speed multiplier.
 *
 * Weaver-only per vocations.md §2.10: cranes fly 2× faster.
 *
 * @param v - The Vocation sending the crane.
 * @param base - Base crane drift speed.
 * @returns The adjusted speed. 2× for Weaver, identity otherwise.
 */
export function applyVocationEffect_craneSpeed(v, base) {
    return v === VocationKey.Weaver ? base * WEAVER_CRANE_SPEED_MULTIPLIER : base;
}
/**
 * Maximum number of recipients a single crane can be addressed to.
 *
 * Weaver-only per vocations.md §2.10: up to 3 recipients vs 1 baseline.
 *
 * @param v - The Vocation sending the crane.
 * @returns The recipient cap.
 */
export function getVocationCraneRecipientCap(v) {
    return v === VocationKey.Weaver ? WEAVER_CRANE_RECIPIENT_CAP : BASELINE_CRANE_RECIPIENT_CAP;
}
/** Pilgrim trail-speed multiplier per vocations.md §2.9. */
export const PILGRIM_TRAIL_SPEED_BONUS = 1.15;
/**
 * Trail-movement speed multiplier.
 *
 * Pilgrim-only per vocations.md §2.9: +15% movement speed on trail tiles.
 * Separate from shrineTickRate (which gives +10% ritual tick).
 */
export function applyVocationEffect_trailSpeed(v, base) {
    return v === VocationKey.Pilgrim ? base * PILGRIM_TRAIL_SPEED_BONUS : base;
}
/** Sleeper whisper-accrual multiplier while dreaming (vocations.md §2.1). */
export const SLEEPER_WHISPER_MULTIPLIER = 2;
/**
 * Whisper resource accrual multiplier.
 *
 * Sleeper-only per vocations.md §2.1: 2× Whisper accrual while in the
 * "Dreaming" attendance state.
 */
export function applyVocationEffect_whisperAccrual(v, base, isDreaming = false) {
    if (v === VocationKey.Sleeper && isDreaming)
        return base * SLEEPER_WHISPER_MULTIPLIER;
    return base;
}
/** Builder material cost multiplier per vocations.md §2.3. */
export const BUILDER_MATERIAL_COST_MULTIPLIER = 0.75;
/**
 * Home material construction cost multiplier.
 *
 * Builder-only per vocations.md §2.3: −25% material cost for home
 * construction/upgrades. Uses Math.ceil to prevent free materials.
 */
export function applyVocationEffect_homeMaterialCost(v, base) {
    return v === VocationKey.Builder ? Math.ceil(base * BUILDER_MATERIAL_COST_MULTIPLIER) : base;
}
/** Fool gather-discovery chance per vocations.md §2.7. */
export const FOOL_GATHER_DISCOVERY_CHANCE = 0.10;
/**
 * Gather-action discovery chance.
 *
 * Fool-only per vocations.md §2.7: 10% chance per gather to produce a Fool
 * Recipe discovery. Extends the existing craft-failure mechanic to gathering.
 */
export function applyVocationEffect_gatherDiscoveryChance(v, base) {
    return v === VocationKey.Fool ? FOOL_GATHER_DISCOVERY_CHANCE : base;
}
/** Minimum continuous stationary milliseconds for Warden's bonus. */
export const WARDEN_STILLNESS_THRESHOLD_MS = 30_000;
/** Warden's resistance multiplier when the stillness gate is met. */
export const WARDEN_RESISTANCE_BONUS = 1.3;
/**
 * Stillness-gated damage and decay resistance multiplier.
 *
 * Warden-only per vocations.md §2.11: +30% damage-and-decay resistance while
 * stationary ≥30 continuous real seconds. Caller supplies base resistance
 * and the character's continuous-stillness duration in ms.
 *
 * @param v - The Vocation standing still.
 * @param base - Base resistance multiplier.
 * @param stillnessMs - How long the character has been stationary (ms).
 * @returns The adjusted resistance. ×1.3 for Warden when still ≥30s, identity otherwise.
 */
export function applyVocationEffect_stationaryResistance(v, base, stillnessMs = 0) {
    if (v === VocationKey.Warden && stillnessMs >= WARDEN_STILLNESS_THRESHOLD_MS) {
        return base * WARDEN_RESISTANCE_BONUS;
    }
    return base;
}
/**
 * "The One Who Tried" 1000-hour latent-attendance modifier.
 *
 * OneWhoTried-only per vocations.md §2.12: at exactly 1,000 total
 * attendance-hours the Vocation begins applying small, undocumented
 * modifications.
 *
 * @param v - The Vocation being ticked.
 * @param base - Base value for whatever stat the caller is routing through.
 * @param totalAttendanceMs - Cumulative online time in milliseconds.
 * @param category - Which effect category to apply.
 * @returns The adjusted value. Identity for non-OneWhoTried or sub-threshold.
 */
export function applyVocationEffect_oneWhoTriedAttendanceMod(v, base, totalAttendanceMs = 0, category) {
    if (v !== VocationKey.OneWhoTried || !category)
        return base;
    return base * getOneWhoTriedMultiplier(totalAttendanceMs, category);
}
//# sourceMappingURL=index.js.map